import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { useAccount } from 'wagmi'
import { EthTokenCreate } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsEthToken/EthTokenCreate'
import { BscTokenCreate } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsBscToken/BscTokenCreate'
import { MaticTokenCreate } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsMaticToken/MaticTokenCreate'
import { EthGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsEthSmartContract/getStatus'
import { MaticGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsMaticSmartContract/maticCheckStatus'
import { BscGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsBscSmartContract/BscGetTxStatus'

import { useAuthState } from 'src/contexts/authContext'
import { axiosInstance } from 'src/util/axios.js'
import { evmData, evmDataType } from 'src/util/networksData'
import { networks, networksType } from 'src/util/networksRpcs'
import { evmIds, evmIdsType } from 'src/util/chainIds'

export interface IUseIssueEVMToken {
  operation: string
  projectType: string
}

export interface IIssueEVMToken {
  tokenSymbol: string
  tokenName: string
  initialSupply: number
  numberOfDecimals: number
  network: string
  chain: string
  canMint: boolean
  canBurn: boolean
}

const ethereum = window.ethereum
const useIssueEsdtToken = ({ operation, projectType }: IUseIssueEVMToken) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isConnected, address } = useAccount()

  const navigate = useNavigate()
  const user = useAuthState().user

  let zoid = new ZoidCore({
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  })

  let txCheckIntervalId: any = null
  const verifyProject = async ({
    projectId,
    txHash,
    chain,
    network,
  }: {
    projectId: string
    txHash: string
    chain: string
    network: string
  }) => {
    const networkData = evmData[chain as keyof evmDataType][network]
    const chainId = evmIds[chain as keyof evmIdsType][network]

    const payload: EthGetTxStatus | BscGetTxStatus | MaticGetTxStatus = {
      address: address || '',
      networkProvider: networkData.rpcUrls[0],
      txhash: txHash,
      environment: network,
      ...(chain === 'ethereum' ? { currentAddress: address } : {}),
      ...(chain === 'ethereum' ? { chainId: chainId } : {}),
    }

    try {
      let txStatus
      switch (chain) {
        case 'ethereum':
          txStatus = await zoid.ethereum.ethTxStatus(payload as EthGetTxStatus)
          break
        case 'bnb':
          txStatus = await zoid.bsc.bscTxStatus(payload as BscGetTxStatus)
          break
        case 'polygon':
          txStatus = await zoid.polygon.polygonGetTxStatus(payload as MaticGetTxStatus)
          break
      }
      if (txStatus?.data?.data?.status) {
        const txReceipt = await zoid.utils.getTxReceipt({
          txHash,
          networkProvider: networkData.rpcUrls[0],
        })

        if (txReceipt?.data?.data) {
          clearInterval(txCheckIntervalId)
          navigate(`/projects/${projectId}`)
        }
      }
    } catch (error) {
      console.error('error', error)
      setIsLoading(false)
      clearInterval(txCheckIntervalId)
      if (error instanceof Error) {
        toast.error(error?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const issueToken = async ({
    tokenSymbol,
    tokenName,
    initialSupply,
    numberOfDecimals,
    canMint,
    canBurn,
    network,
    chain,
  }: IIssueEVMToken) => {
    setIsLoading(true)
    const networkData = evmData[chain as keyof evmDataType][network]
    const chainId = evmIds[chain as keyof evmIdsType][network]

    const payload = {
      address,
      currentAddress: address,
      tokenName,
      tokenSymbol,
      networkProvider: networkData.rpcUrls[0],
      supply: Number(initialSupply),
      numberOfDecimals: Number(numberOfDecimals),
      canMint,
      canBurn,
      environment: network,
      ...(chain === 'ethereum' ? { chainId: chainId } : {}),
    }
    try {
      let response
      switch (chain) {
        case 'ethereum':
          response = await zoid.ethereum.ethCreateToken(payload as EthTokenCreate)
          break
        case 'bnb':
          response = await zoid.bsc.bscIssueToken(payload as BscTokenCreate)
          break
        case 'polygon':
          response = await zoid.polygon.polygonCreateToken(payload as MaticTokenCreate)
          break
      }

      const { data, from }: { data: string; from: string } = response?.data?.data?.tx || {}
      const params = {
        from,
        data,
      }

      const txHash = (await ethereum?.request({
        method: 'eth_sendTransaction',
        params: [params],
      })) as string

      const projectResponse = await axiosInstance.post(
        '/blockchain-helpers/external/createProject',
        {
          data: {
            ...payload,
            chain,
            network,
            operation,
            verificationNetwork: networks[chain as keyof networksType][network],
          },
          txHash,
          projectType,
          abi: response?.data?.data?.abi,
        },
      )

      txCheckIntervalId = setInterval(() => {
        verifyProject({
          projectId: projectResponse?.data?.data?.id,
          txHash,
          chain,
          network,
        })
      }, 10000)
    } catch (err) {
      setIsLoading(false)
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return { isLoading, isWalletConnected: isConnected, issueToken }
}

export default useIssueEsdtToken
